<template>
  <b-modal
    ref="modal"
    centered
    size="xl"
    body-class="pb-0"
    :hide-footer="initializing"
    footer-class="d-block pb-0"
    :title="$t('transports.transportLicense')"
    @hide="resetModal"
  >
    <div v-if="initializing" class="text-center mb-3">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-card
        bg-variant="light"
        :header="$t('transports.driverInfo')"
        class="border-0"
        header-class="border-bottom-0"
        body-class="pb-0"
      >
        <b-row>
          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('account.firstName')">
                <b-input v-model="model.driverFirstName"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('account.lastName')">
                <b-input v-model="model.driverLastName"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('account.nationalCode')">
                <b-input v-model="model.driverNationalCode"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('account.phoneNumber')">
                <b-input v-model="model.driverPhoneNumber"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.licenseNumber')">
                <b-input v-model="model.driverLicenseNumber"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.smartNumber')">
                <b-input v-model="model.driverSmartNumber"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.issuePlace')">
                <b-input v-model="model.driverIssuePlace"/>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        bg-variant="light"
        :header="$t('transports.licenseValidity')"
        class="border-0"
        header-class="border-bottom-0"
        body-class="pb-0"
      >
        <b-row>
          <b-col md="6">
            <b-form-group>
              <date-picker
                required
                :label="$t('transports.fromDate')"
                time
                :name="`${$t('transports.licenseValidity')} (${$t('transports.fromDate')})`"
                v-validate="'required'"
                v-model="model.validFrom"
              />
              <!-- :available-date-start="transport.transportDate" -->
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group>
              <date-picker
                required
                :label="$t('transports.toDate')"
                time
                :name="`${$t('transports.licenseValidity')} (${$t('transports.toDate')})`"
                v-validate="'required'"
                v-model="model.validUntil"
              />
              <!-- :available-date-start="transport.transportDate" -->
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        bg-variant="light"
        :header="$t('transports.carInfo')"
        class="border-0"
        header-class="border-bottom-0"
        body-class="pb-0"
      >
        <b-row>
          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('shared.name')">
                <b-input v-model="model.vehicleName"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.trailerName')">
                <b-input v-model="model.trailerName"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.disciplinaryNumber')">
                <b-input-group-prepend is-text>
                  {{ $t('transports.iran') }}
                </b-input-group-prepend>

                <b-input
                  class="text-center"
                  placeholder="xx"
                  v-model="model.plateCode"
                />

                <b-input
                  class="text-center"
                  placeholder="‍‍‍xxx د xx"
                  v-model="model.plateNumber"
                />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="4" md="6">
            <b-form-group>
              <b-input-group :prepend="$t('transports.smartNumber')">
                <b-input v-model="model.vehicleSmartNumber"/>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <b-form-group>
        <b-input-group :prepend="$t('transports.cargoRow')">
          <b-input v-model="model.cargoRow"/>
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <b-input-group :prepend="$t('shared.description')">
          <b-textarea
            :rows="3"
            v-model="model.comments"
          />
        </b-input-group>
      </b-form-group>
    </template>

    <template v-slot:modal-footer>
      <b-alert :show="errors.any()" variant="danger" class="px-4">
        <ul class="m-0 px-1">
          <li v-for="err in errors.all()" :key="err">{{err}}</li>
        </ul>
      </b-alert>

      <b-btn
        :disabled="progressing"
        variant="success"
        class="mb-3"
        size="lg"
        @click.prevent="submit"
      >
        <font-awesome-icon
          :icon="progressing ? 'cog' : 'plus'"
          :spin="progressing"
          class="align-middle"
          :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
        />

        {{ $t('shared.create') }}
      </b-btn>
    </template>
  </b-modal>
</template>

<script>
import { DatePicker } from '@/components/shared'

export default {
  name: 'CreateTransportLicense',
  components: {
    DatePicker
  },
  data () {
    return {
      initializing: true,
      progressing: false,
      transport: null,
      model: {
        cargoRow: null,
        comments: null,
        driverFirstName: null,
        driverIssuePlace: null,
        driverLastName: null,
        driverLicenseNumber: null,
        driverNationalCode: null,
        driverPhoneNumber: null,
        driverSmartNumber: null,
        plateCode: null,
        plateNumber: null,
        trailerName: null,
        validFrom: null,
        validUntil: null,
        vehicleName: null,
        vehicleSmartNumber: null
      }
    }
  },
  methods: {
    show (transport) {
      this.transport = transport
      this.$refs.modal.show()
      this.initializing = false
    },
    resetModal () {
      this.initializing = true
      this.progressing = false
      this.transport = null
    },
    getUserDisplayName (user) {
      return this.$api.account.getUserDisplayName(user)
    },
    submit () {
      this.$validator.validateAll()
      .then(isValid => {
        if (isValid) {
          this.progressing = true

          this.$api.transports.createLicense(this.transport.id, this.model)
          .then(() => {
            this.progressing = false
            this.$showSuccess(this.$t('transports.transportLicenseAdded'))
            this.$emit('refresh')
            this.$refs.modal.hide()
          })
          .catch(err => {
            this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
            this.$refs.modal.hide()
          })
        }
      })
    }
  }
}
</script>
